import React, { useEffect, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import Chart from 'react-apexcharts';
import { useReportes } from './ReportesContex';
import '../../styles/reportes.css';

const CUMPLIMIENTO_ALERTA = 80;
const COLOR_ALERTA = '#fe424d';
const COLOR_META = '#8a8fa3';

// Paleta categórica: cada SKU apilado recibe un tono distinto, en orden fijo.
// Validada sobre fondo blanco (peor par adyacente ΔE 24.2 en daltonismo, objetivo >= 12).
// Los tonos NO se ciclan: si hay más SKUs que ranuras, los de menor volumen van a «Otros»,
// porque repetir un color entre dos productos distintos se lee como si fueran el mismo.
const PALETA_SKU = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'];
const COLOR_OTROS = '#898781';
const ETIQUETA_OTROS = 'Otros';

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function fmtFecha(iso) {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}

function fmtFechaCorta(iso) {
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
}

function fmtPct(val) {
    return val === null ? '—' : `${val.toFixed(1)}%`;
}

// Un día sin meta no tiene cumplimiento definido: no es un día "bajo meta".
function bajoMeta(pct) {
    return pct !== null && pct < CUMPLIMIENTO_ALERTA;
}

function etiquetaProducto(sku, nombre) {
    return [sku, nombre].filter(Boolean).join(' · ');
}

// El servicio devuelve un renglón por (día, SKU). Aquí se consolida a un renglón por día,
// que es lo que piden la tabla y el Excel, conservando el detalle por SKU para las barras apiladas.
function agruparPorDia(rows) {
    const porDia = new Map();
    rows.forEach((r) => {
        if (!r.fecha) return;
        if (!porDia.has(r.fecha)) {
            porDia.set(r.fecha, { fecha: r.fecha, productos: [], unidadesProducidas: 0, meta: 0 });
        }
        const dia = porDia.get(r.fecha);
        dia.productos.push({ sku: r.sku, nombre: r.nombre, unidades: r.unidadesProducidas, meta: r.meta });
        dia.unidadesProducidas += r.unidadesProducidas;
        dia.meta += r.meta;
    });
    return Array.from(porDia.values())
        .sort((a, b) => a.fecha.localeCompare(b.fecha))
        .map((dia) => ({
            ...dia,
            producto: dia.productos.map((p) => etiquetaProducto(p.sku, p.nombre)).join(', '),
            porcentajeCumplimiento: dia.meta > 0 ? (dia.unidadesProducidas / dia.meta) * 100 : null,
        }));
}

// Columnas apiladas por SKU + meta como línea punteada, todo sobre el eje de unidades.
// ApexCharts no soporta un segundo eje Y en gráficos apilados, así que el % de cumplimiento
// no va como serie: vive en los KPIs y en la tabla. Los días bajo el umbral se marcan con
// un punto rojo sobre la línea de meta.
function buildChartOptions({ categories, colors, discrete, unidadesMax, totalSkus }) {
    return {
        chart: {
            type: 'line',
            stacked: true,
            toolbar: { show: false },
            zoom: { enabled: false },
            parentHeightOffset: 0,
            fontFamily: 'WorkSans',
            animations: { enabled: true, easing: 'easeinout', speed: 600 },
        },
        colors,
        // Borde blanco de 2px en las columnas: separa los segmentos apilados entre sí.
        // La última entrada corresponde a la línea de meta, que conserva su grosor y color.
        stroke: {
            show: true,
            width: [...Array(totalSkus).fill(2), 3],
            colors: [...Array(totalSkus).fill('#fff'), COLOR_META],
            curve: 'straight',
            dashArray: [...Array(totalSkus).fill(0), 5],
        },
        plotOptions: {
            bar: { columnWidth: '68%', borderRadius: 3, borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last' },
        },
        // ApexCharts rellena las columnas al 85% por defecto y eso aclara los tonos de la
        // paleta; a opacidad 1 se dibujan tal como se validaron sobre el fondo blanco.
        fill: { opacity: 1 },
        markers: { size: 0, strokeWidth: 2, strokeColors: '#fff', discrete, hover: { sizeOffset: 2 } },
        dataLabels: { enabled: false },
        // Sin fontSize: ApexCharts aplica el de la leyenda como estilo inline haciendo
        // parseFloat + 'px', así que un valor en rem se convierte en «0.8125px» y el texto
        // desaparece. Su valor por defecto (12px) coincide con el de los ejes.
        legend: { show: true, position: 'top', horizontalAlign: 'right', fontFamily: 'WorkSans', markers: { radius: 4 } },
        xaxis: {
            categories,
            labels: { style: { colors: '#5f606f', fontFamily: 'WorkSans', fontSize: '0.75rem' }, rotate: -45, rotateAlways: false, hideOverlappingLabels: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            min: 0,
            max: unidadesMax,
            title: { text: 'Unidades', style: { color: '#5f606f', fontFamily: 'WorkSans', fontWeight: 600 } },
            labels: { formatter: (v) => Number(v).toLocaleString(), style: { colors: '#5f606f', fontFamily: 'WorkSans', fontSize: '0.75rem' } },
        },
        grid: { borderColor: '#e9ecf0', padding: { right: 8, left: 8 } },
        tooltip: {
            shared: true,
            intersect: false,
            y: { formatter: (val) => (val === null || val === undefined ? '—' : Number(val).toLocaleString()) },
        },
        noData: {
            text: 'No existen datos para esta selección',
            align: 'center',
            verticalAlign: 'middle',
            style: { fontSize: '1.125rem', fontFamily: 'WorkSans', fontWeight: 600, color: '#5f606f' },
        },
    };
}

async function exportarExcel(dias, txtRange, filtroTxt) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const toArgb = (hex) => `FF${hex.toUpperCase()}`;
    const primary = getPrimaryColor();
    const headerBg = blendHex(primary, '000000', 0.8);
    const infoBg = blendHex(primary, 'ffffff', 0.85);
    const alertBg = 'FFFBE2E2';

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Producción diaria');

    const infoText = [txtRange ? `Rango: ${txtRange}` : '', filtroTxt].filter(Boolean).join('   |   ');
    if (infoText) {
        ws.mergeCells('A1:E1');
        ws.getCell('A1').value = infoText;
        ws.getCell('A1').style = {
            font: { size: 11 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } },
        };
        ws.getRow(1).height = 18;
    }

    const hdrStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(headerBg) } },
        alignment: { horizontal: 'center', vertical: 'middle' },
    };
    const hdrs = ['Fecha', 'Producto(s)', 'Unidades producidas', 'Meta del día', '% Cumplimiento'];
    const hdrRowIdx = infoText ? 2 : 1;
    const hdrRow = ws.getRow(hdrRowIdx);
    hdrs.forEach((h, i) => { hdrRow.getCell(i + 1).value = h; hdrRow.getCell(i + 1).style = hdrStyle; });
    hdrRow.height = 20;
    [12, 44, 18, 16, 16].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    dias.forEach((item, i) => {
        const row = ws.getRow(hdrRowIdx + 1 + i);
        const isAlert = bajoMeta(item.porcentajeCumplimiento);
        row.getCell(1).value = fmtFecha(item.fecha);
        row.getCell(2).value = item.producto;
        row.getCell(3).value = item.unidadesProducidas;
        row.getCell(3).numFmt = '#,##0';
        row.getCell(4).value = item.meta;
        row.getCell(4).numFmt = '#,##0';
        if (item.porcentajeCumplimiento === null) {
            row.getCell(5).value = '—';
        } else {
            row.getCell(5).value = item.porcentajeCumplimiento / 100;
            row.getCell(5).numFmt = '0.0%';
        }
        const fillArgb = isAlert ? alertBg : (i % 2 !== 0 ? 'FFF7F8FE' : 'FFFFFFFF');
        row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } }; });
        row.height = 16;
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `produccion_diaria_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export default function InformeProduccionDiaria() {
    const {
        informeProduccionDiariaData,
        isLoadingLlamadas,
        txtRange,
        selectedLinea,
        setExportFn,
    } = useReportes();

    const dias = useMemo(() => agruparPorDia(informeProduccionDiariaData), [informeProduccionDiariaData]);
    const hayDatos = dias.length > 0;

    const filtroTxt = useMemo(
        () => (selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : ''),
        [selectedLinea]
    );

    const chart = useMemo(() => {
        const categories = dias.map((d) => fmtFechaCorta(d.fecha));

        const totalPorSku = new Map();
        informeProduccionDiariaData.forEach((r) => {
            const label = etiquetaProducto(r.sku, r.nombre);
            if (label) totalPorSku.set(label, (totalPorSku.get(label) || 0) + r.unidadesProducidas);
        });

        // Orden alfabético: el color sigue al producto y no a su volumen, así al cambiar el
        // rango de fechas los SKUs que siguen presentes conservan su tono.
        let etiquetas = Array.from(totalPorSku.keys()).sort((a, b) => a.localeCompare(b));
        let plegados = [];
        if (etiquetas.length > PALETA_SKU.length) {
            const visibles = [...totalPorSku.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, PALETA_SKU.length - 1)
                .map(([label]) => label);
            plegados = etiquetas.filter((l) => !visibles.includes(l));
            etiquetas = etiquetas.filter((l) => visibles.includes(l));
        }

        // Los días sin ese SKU quedan en null para no dibujar un segmento vacío.
        const unidadesDe = (dia, incluye) => {
            const match = dia.productos.filter((p) => incluye(etiquetaProducto(p.sku, p.nombre)));
            return match.length > 0 ? match.reduce((a, p) => a + p.unidades, 0) : null;
        };

        const seriesSku = etiquetas.map((label) => ({
            name: label,
            type: 'column',
            data: dias.map((d) => unidadesDe(d, (l) => l === label)),
        }));
        if (plegados.length > 0) {
            seriesSku.push({
                name: `${ETIQUETA_OTROS} (${plegados.length})`,
                type: 'column',
                data: dias.map((d) => unidadesDe(d, (l) => plegados.includes(l))),
            });
        }

        // Punto rojo sobre la línea de meta (última serie) en los días que no alcanzan el umbral.
        const discrete = dias
            .map((d, i) =>
                bajoMeta(d.porcentajeCumplimiento)
                    ? { seriesIndex: seriesSku.length, dataPointIndex: i, fillColor: COLOR_ALERTA, strokeColor: '#fff', size: 6 }
                    : null
            )
            .filter(Boolean);

        // Máximo explícito: ApexCharts suma la línea de meta al total apilado y deja el eje inflado.
        const topStack = Math.max(...dias.map((d) => d.unidadesProducidas), ...dias.map((d) => d.meta), 0);
        const unidadesMax = topStack > 0 ? Math.ceil((topStack * 1.15) / 5) * 5 : undefined;

        const coloresSku = etiquetas.map((_, i) => PALETA_SKU[i]);
        if (plegados.length > 0) coloresSku.push(COLOR_OTROS);

        return {
            categories,
            discrete,
            unidadesMax,
            totalSkus: seriesSku.length,
            series: [
                ...seriesSku,
                { name: 'Meta', type: 'line', data: dias.map((d) => d.meta) },
            ],
            colors: [...coloresSku, COLOR_META],
        };
    }, [dias, informeProduccionDiariaData]);

    const kpis = useMemo(() => {
        const totalProducido = dias.reduce((a, d) => a + d.unidadesProducidas, 0);

        // El cumplimiento sólo se mide sobre los días que tienen meta: contar la producción de
        // un día sin meta contra el total infla el porcentaje (p. ej. una jornada de 322 unidades
        // con meta 0 llevaba el global a 181% con 7 de 11 días en rojo).
        const conMeta = dias.filter((d) => d.meta > 0);
        const metaAcumulada = conMeta.reduce((a, d) => a + d.meta, 0);
        const producidoConMeta = conMeta.reduce((a, d) => a + d.unidadesProducidas, 0);
        const cumplimientoGlobal = metaAcumulada > 0 ? (producidoConMeta / metaAcumulada) * 100 : null;

        const diasBajoMeta = dias.filter((d) => bajoMeta(d.porcentajeCumplimiento)).length;
        return { totalProducido, cumplimientoGlobal, diasBajoMeta, totalDias: conMeta.length };
    }, [dias]);

    useEffect(() => {
        if (hayDatos) {
            setExportFn(() => () => exportarExcel(dias, txtRange, filtroTxt));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [hayDatos, dias, txtRange, filtroTxt, setExportFn]);

    if (isLoadingLlamadas) {
        return (
            <div className="ipd-state">
                <CircularProgress className="ipd-spinner" />
            </div>
        );
    }

    if (!hayDatos) {
        return (
            <div className="ipd-state">
                Selecciona los filtros y presiona «Ver Reporte»
            </div>
        );
    }

    return (
        <div className="ipd-container">
            <div className="ipd-kpi-row">
                <div className="ipd-kpi-card">
                    <span className="ipd-kpi-value">{kpis.totalProducido.toLocaleString()}</span>
                    <span className="ipd-kpi-label">Unidades producidas</span>
                </div>
                <div className="ipd-kpi-card">
                    <span className={`ipd-kpi-value${bajoMeta(kpis.cumplimientoGlobal) ? ' ipd-kpi-value--alert' : ''}`}>
                        {fmtPct(kpis.cumplimientoGlobal)}
                    </span>
                    <span className="ipd-kpi-label">Cumplimiento global</span>
                </div>
                <div className="ipd-kpi-card">
                    <span className={`ipd-kpi-value${kpis.diasBajoMeta > 0 ? ' ipd-kpi-value--alert' : ''}`}>
                        {kpis.diasBajoMeta}
                        <span className="ipd-kpi-value-sub">/{kpis.totalDias}</span>
                    </span>
                    <span className="ipd-kpi-label">Días bajo meta</span>
                </div>
            </div>

            <div className="ipd-chart-card">
                <span className="ipd-table-title">
                    Producción vs. meta diaria{filtroTxt ? ` · ${filtroTxt}` : ''}
                </span>
                <Chart
                    options={buildChartOptions({
                        categories: chart.categories,
                        colors: chart.colors,
                        discrete: chart.discrete,
                        unidadesMax: chart.unidadesMax,
                        totalSkus: chart.totalSkus,
                    })}
                    series={chart.series}
                    type="line"
                    width="100%"
                    height={360}
                />
            </div>

            <div className="ipd-table-toolbar">
                <span className="ipd-table-title">Producción diaria{filtroTxt ? ` · ${filtroTxt}` : ''}</span>
            </div>
            <div className="ipd-table-card">
                <table className="ipd-table">
                    <thead>
                        <tr>
                            <th className="ipd-th">Fecha</th>
                            <th className="ipd-th">Producto(s)</th>
                            <th className="ipd-th ipd-th-right">Unidades producidas</th>
                            <th className="ipd-th ipd-th-right">Meta del día</th>
                            <th className="ipd-th ipd-th-right">% Cumplimiento</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dias.map((item) => {
                            const rowCls = bajoMeta(item.porcentajeCumplimiento) ? 'ipd-row ipd-row--alert' : 'ipd-row';
                            return (
                                <tr key={item.fecha} className={rowCls}>
                                    <td className="ipd-td">{fmtFecha(item.fecha)}</td>
                                    <td className="ipd-td">{item.producto}</td>
                                    <td className="ipd-td ipd-th-right">{item.unidadesProducidas.toLocaleString()}</td>
                                    <td className="ipd-td ipd-th-right">{item.meta.toLocaleString()}</td>
                                    <td className="ipd-td ipd-th-right ipd-td-pct">{fmtPct(item.porcentajeCumplimiento)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
